/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';
import {useGetSubAccounts} from '@ion/query/src/hooks/useGetSubAccounts';
import Dropdown, {createOptions} from '../components/menus/Dropdown';

/**
 * The AccountsChooserProps interface defines the set of props that
 * can be passed as parameters to the AccountsChooser component.
 *
 * `account` (AccountLink, required): The parent account for which sub-accounts are displayed.
 * `value` (string[], optional): The selected account values.
 * `onChange` (Function, optional): A callback function triggered on value change.
 * `disabled` (boolean, optional): Indicates whether the component is disabled.
 * `hasError` (boolean, optional): Indicates whether the component has an error.
 * `defaultValue` (string[], optional, default: []): The default selected values.
 * `errorText` (string, optional): The error text to display when an error occurs.
 */
export interface AccountsChooserProps {
  account: AccountLink;
  value?: string[];
  onChange?: Function;
  disabled?: boolean;
  hasError?: boolean;
  defaultValue?: string[];
  errorText?: string;
};

/**
 * The AccountsChooser component allows users to choose sub-accounts from a Dropdown.
 * It displays a list of sub-accounts related to a parent account.
 *
 * ```typescript
 *
 *  import {AccountsChooser} from '@ion/dls/src/editors/';
 *
 *  <AccountsChooser account={account} />
 *
 * ```
 */
export const AccountsChooser = ({
  account,
  value,
  onChange,
  disabled,
  hasError,
  errorText,
  defaultValue = []
}: AccountsChooserProps) => {
  const [selected, setSelected] = useState(value || []);

  // useEffect to update the selected state when external value changes.
  useEffect(() => {
    if (!isEqual(value, selected)) {
      setSelected(value);
    }
  }, [value]);

  // useGetSubAccounts hook fetches sub-accounts for the specified parent account.
  const {
    subAccounts,
    isLoading
  } = useGetSubAccounts({account, enabled: !!entityId(account)});

  // useEffect to update the selected state when sub-accounts list has a change.
  useEffect(() => {
    if (!isLoading) {
      handleOnChange({
        target : {value: selected}
      });
    }
  }, [subAccounts, isLoading]);

  const options = useMemo(() => (
    createOptions({
      items     : subAccounts,
      labelProp : 'title',
      valProp   : 'href',
      augmentor : (_item, {label, value}) => ({
        value : entityId(value),
        label
      })
    })
  ), [subAccounts]);

  const handleOnChange = useCallback(({target: {value}}) => {
    const selectedIds = value?.map(account => account?.value || account);
    setSelected(selectedIds);
    onChange?.(selectedIds);
  }, [account, onChange]);

  const onClearAll = useCallback(() => {
    setSelected([]);
    onChange?.([]);
  }, []);

  const onSelectAll = useCallback(() => {
    const ids = options?.map((option) => option.value || option);
    setSelected(ids);
    onChange?.(ids);
  }, [options]);

  const filtered = useMemo(() => {
    const accountId = entityId(account);
    return selected?.filter?.(item => item !== accountId) || selected;
  }, [selected, account]);

  return (
    <Dropdown placeholder={translate('common:accountChooser.placeholder')}
      fieldLabel={translate(`common:accountChooser.${filtered?.length ? 'countLabel' : 'label'}`, {count: filtered?.length})}
      disabled={isLoading || disabled}
      defaultValue={defaultValue}
      onSelectAll={onSelectAll}
      onChange={handleOnChange}
      onClearAll={onClearAll}
      errorText={errorText}
      hasError={hasError}
      options={options}
      value={filtered}
      limitTags={3}
      includeSelectAll
      multiSelect />
  );
};

export default AccountsChooser;
